import { createContext, useContext, useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  fetchAnnouncements,
  markAnnouncementsRead,
  openAnnouncementStream,
  toggleReaction,
} from '../services/announcements';
import { playAnnouncementChime } from '../utils/announcementChime';

export const ANNOUNCEMENTS_PATH = '/dashboard/announcements';

/* Reconnect delay after the stream closes for good (an expired token, the api
   restarting). Long enough not to hammer a server that is still coming back. */
const RECONNECT_MS = 5000;

const AnnouncementsContext = createContext({
  announcements: [],
  unreadCount:   0,
  loading:       false,
  error:         '',
  toast:         null,
  dismissToast:  () => {},
  markAllRead:   () => {},
  react:         () => {},
});

export const useAnnouncements = () => useContext(AnnouncementsContext);

export function AnnouncementsProvider({ children }) {
  const location = useLocation();
  /* Read on every render rather than once: the provider outlives the login
     screen, and re-reading is how it notices the token that login just wrote.
     Route changes re-render it, and logging in always changes the route. */
  const token = localStorage.getItem('token');

  const [announcements, setAnnouncements] = useState([]);
  const [unreadCount, setUnreadCount]     = useState(0);
  const [loading, setLoading]             = useState(false);
  const [error, setError]                 = useState('');
  const [toast, setToast]                 = useState(null);
  const [streamEpoch, setStreamEpoch]     = useState(0);

  /* The stream handlers are created once per token, so anything they need to
     know about the *current* render goes through a ref. */
  const viewingRef = useRef(false);
  viewingRef.current = location.pathname === ANNOUNCEMENTS_PATH;
  const retryRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchAnnouncements();
      setAnnouncements(data.announcements);
      setUnreadCount(viewingRef.current ? 0 : data.unreadCount);
      setError('');
    } catch (err) {
      /* A 401 is already being handled by the api interceptor (refresh, then
         session-expired), so there is nothing useful to show for it. */
      if (err.response?.status !== 401) setError('Could not load announcements.');
    } finally {
      setLoading(false);
    }
  }, []);

  const markAllRead = useCallback(async () => {
    setUnreadCount(0);
    try {
      await markAnnouncementsRead();
    } catch (err) {
      console.error('Failed to mark announcements read:', err);
    }
  }, []);

  const react = useCallback(async (id, emoji) => {
    try {
      const { reactions, myReactions } = await toggleReaction(id, emoji);
      /* Merge the reaction fields rather than swapping the whole object in:
         replacing it would also swap the attachment URLs, and a changed
         <img src> makes the browser refetch a picture nothing happened to. */
      setAnnouncements(list => list.map(a => (
        a.id === id ? { ...a, reactions, myReactions } : a
      )));
    } catch (err) {
      console.error('Failed to save reaction:', err);
    }
  }, []);

  const dismissToast = useCallback(() => setToast(null), []);

  /* Initial load, and a reload whenever the stream had to be rebuilt — a
     dropped connection may have missed events, and the fetch is the catch-up. */
  useEffect(() => {
    if (!token) {
      setAnnouncements([]);
      setUnreadCount(0);
      setToast(null);
      return;
    }
    load();
  }, [token, streamEpoch, load]);

  useEffect(() => {
    if (!token) return;

    const close = openAnnouncementStream(
      {
        'announcement:new': announcement => {
          setAnnouncements(list =>
            list.some(a => a.id === announcement.id) ? list : [...list, announcement]
          );
          /* Already reading the channel: it is read, and a toast pointing at
             the page you are on is noise. */
          if (viewingRef.current) {
            markAllRead();
          } else {
            setUnreadCount(count => count + 1);
            setToast(announcement);
            playAnnouncementChime();
          }
        },
        /* Editing in Slack can add or remove files, so the payload carries the
           whole attachment set — the freshly signed URLs included. */
        'announcement:updated': ({ id, text, editedAt, attachments }) => {
          setAnnouncements(list => list.map(a => (
            a.id === id ? { ...a, text, editedAt, attachments: attachments ?? a.attachments } : a
          )));
        },
        'announcement:deleted': ({ id }) => {
          setAnnouncements(list => list.filter(a => a.id !== id));
          setToast(current => (current?.id === id ? null : current));
        },
        /* Counts are shared by every viewer; myReactions is not, so keep the
           copy this browser already has. */
        'announcement:reaction': ({ id, reactions }) => {
          setAnnouncements(list => list.map(a => (a.id === id ? { ...a, reactions } : a)));
        },
      },
      () => {
        retryRef.current = setTimeout(() => setStreamEpoch(e => e + 1), RECONNECT_MS);
      }
    );

    return () => {
      clearTimeout(retryRef.current);
      close();
    };
  }, [token, streamEpoch, markAllRead]);

  return (
    <AnnouncementsContext.Provider
      value={{ announcements, unreadCount, loading, error, toast, dismissToast, markAllRead, react }}
    >
      {children}
    </AnnouncementsContext.Provider>
  );
}
